import { useState, useEffect } from "react";
import axios from "axios";
import AsyncSelect from "react-select/async";
import Sidebar from "../../../components/sidebar/Sidebar";
import LoadingSpinner from "../../../components/LoadingSpinner";
import { getSelectStyles } from "../../../utils/selectStyles";
import "../../accounts/create/CreateAccountPage.css";

export default function CreateReferralPage() {
  const [accounts, setAccounts]     = useState([]);
  const [created, setCreated]       = useState(false);
  const [loading, setLoading]       = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError]           = useState(null);

  const [referral, setReferral] = useState({ referer: "", refered: "", referDate: null, notes: "" });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await axios.get(`${process.env.REACT_APP_API_URL}/account`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
        setAccounts(res.data);
        setLoading(false);
      } catch (err) {
        setError(err.response?.data?.message || "Failed to load accounts");
      }
    };
    fetchData();
  }, []);

  const loadOptions = (inputValue, callback) => {
    const filtered = accounts.filter(a =>
      a.username.toLowerCase().includes(inputValue.toLowerCase()) ||
      a.email?.toLowerCase().includes(inputValue.toLowerCase())
    );
    callback(filtered.map(a => ({ value: a._id, label: `${a.username}${a.email ? ` (${a.email})` : ''}` })));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'referDate') {
      setReferral(prev => ({ ...prev, [name]: value ? new Date(value).getTime() : null }));
    } else {
      setReferral(prev => ({ ...prev, [name]: value }));
    }
  };

  const formatDateForInput = (unixMs) => unixMs ? new Date(unixMs).toISOString().split('T')[0] : '';

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!referral.referer) { setError("Please select the referer account"); return; }
    if (!referral.refered) { setError("Please select the referred account"); return; }
    setError(null);
    setCreated(false);
    setSubmitting(true);
    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/referral/create`,
        { referer: referral.referer, refered: referral.refered, notes: referral.notes, referDate: referral.referDate + "" },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      setCreated(true);
      setReferral({ referer: "", refered: "", referDate: null, notes: "" });
    } catch (err) {
      setError(err.response?.data?.message || "Failed to create referral");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <>
      <Sidebar title="Create Referral" />
      <div className="form-page">
        <div className="form-panel">

          <div className="form-panel-header">
            <div>
              <div className="form-panel-title">Create New Referral</div>
              <div className="form-panel-subtitle">Link two accounts with a referral relationship</div>
            </div>
          </div>

          <div className="form-panel-body">
            <form onSubmit={handleSubmit} noValidate>

              <div className="form-section-label">Accounts</div>
              <div className="row g-4 mb-2">
                <div className="col-md-6">
                  <div className="form-field">
                    <label className="form-field-label"><i className="bi bi-person-fill" />Referer <span className="required-star">*</span></label>
                    <AsyncSelect cacheOptions defaultOptions loadOptions={loadOptions}
                      onChange={opt => setReferral(prev => ({ ...prev, referer: opt?.value || "" }))}
                      placeholder="Search account…" isClearable styles={getSelectStyles()} />
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="form-field">
                    <label className="form-field-label"><i className="bi bi-person-check-fill" />Referred <span className="required-star">*</span></label>
                    <AsyncSelect cacheOptions defaultOptions loadOptions={loadOptions}
                      onChange={opt => setReferral(prev => ({ ...prev, refered: opt?.value || "" }))}
                      placeholder="Search account…" isClearable styles={getSelectStyles()} />
                  </div>
                </div>
              </div>

              <div className="form-section-label" style={{ marginTop: 8 }}>Details</div>
              <div className="row g-4">
                <div className="col-md-6">
                  <div className="form-field">
                    <label className="form-field-label"><i className="bi bi-calendar-event" />Referral Date <span className="required-star">*</span></label>
                    <input className="form-input" type="date" name="referDate" value={formatDateForInput(referral.referDate)} onChange={handleChange} required />
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="form-field">
                    <label className="form-field-label"><i className="bi bi-journal-text" />Notes</label>
                    <textarea className="form-input" name="notes" value={referral.notes || ''} onChange={handleChange}
                      rows={3} placeholder="Additional notes…" style={{ resize: 'vertical' }} />
                  </div>
                </div>
              </div>

              {created && <div className="form-banner success"><i className="bi bi-check-circle-fill" />Referral created successfully!</div>}
              {error   && <div className="form-banner error"><i className="bi bi-exclamation-triangle-fill" />{error}</div>}

              <div className="form-actions">
                <button type="button" className="btn-form-cancel" onClick={() => window.history.back()}>
                  <i className="bi bi-x-lg" />Cancel
                </button>
                <button type="submit" className="btn-form-submit" disabled={submitting}>
                  <i className={`bi ${submitting ? 'bi-hourglass-split' : 'bi-share-fill'}`} />
                  {submitting ? 'Creating…' : 'Create Referral'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
}
